/**
 * ============================================================
 * ELOISE ACCESSOIRES — gallery.js
 * Galerie produit + sélecteur de variantes
 *
 * Chargé uniquement sur template == 'product' (theme.liquid).
 *
 * Responsabilités :
 *   - Changement d'image au clic sur miniature
 *   - Synchronisation image ↔ variante sélectionnée
 *   - Mise à jour prix + état stock lors du changement de variante
 *   - Mise à jour du champ variant_id caché dans le formulaire
 * ============================================================
 */

'use strict';


// ============================================================
// INIT
// ============================================================

document.addEventListener('DOMContentLoaded', () => {

  initGallery();
  initVariantSelector();
  initSocleSelector();

});


// ============================================================
// GALERIE — Changement d'image principal
// ============================================================

function initGallery() {

  const wrappers = document.querySelectorAll('[data-gallery-wrapper]');
  if (!wrappers.length) return;

  wrappers.forEach(wrapper => {

    const thumbs    = wrapper.querySelectorAll('[data-gallery-thumb]');
    const mainInner = wrapper.querySelector('[data-gallery-main-inner]');

    if (!thumbs.length || !mainInner) return;

    thumbs.forEach(thumb => {

      thumb.addEventListener('click', () => {
        const imageUrl = thumb.dataset.galleryImageUrl;
        const srcset   = thumb.dataset.galleryImageSrcset;

        if (!imageUrl) return;

        // Transition fondu sur l'image principale
        const currentImg = mainInner.querySelector('img');
        if (currentImg) {
          currentImg.style.opacity = '0';
          setTimeout(() => {
            currentImg.src    = imageUrl;
            currentImg.srcset = srcset || '';
            currentImg.style.opacity = '1';
          }, 150);
        }

        // Activer la miniature cliquée
        thumbs.forEach(t => t.classList.remove('product-gallery__thumb--active'));
        thumb.classList.add('product-gallery__thumb--active');
      });

    });

  });
}


// ============================================================
// VARIANTES — Mise à jour prix, stock, image
// ============================================================

function initVariantSelector() {

  const forms = document.querySelectorAll('[data-product-form]');
  if (!forms.length) return;

  forms.forEach(form => {

    // sectionId transmis explicitement via data-section-id sur le formulaire
    const sectionId = form.dataset.sectionId || '';
    const jsonEl    = document.getElementById(`product-variants-${sectionId}`);

    if (!jsonEl) return;

    let variants;
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch {
      return;
    }

    const optionSelects = form.querySelectorAll('[data-option-selector]');
    if (!optionSelects.length) return;

    optionSelects.forEach(select => {
      select.addEventListener('change', () => {
        const selectedOptions = Array.from(
          form.querySelectorAll('[data-option-selector]')
        ).map(s => s.value);

        const matched = variants.find(v =>
          selectedOptions.every((val, i) => v[`option${i + 1}`] === val)
        );

        if (matched) updateVariantUI(form, matched);
      });
    });

  });
}


// ============================================================
// COULEUR DU SOCLE — Pastilles + aperçu (hover desktop / clic mobile)
//
// Sélecteur premium qui remplace le <select> pour l'option
// « Couleur du socle ». Met à jour la variante réelle (champ id caché)
// sans toucher la galerie photo principale.
// ============================================================

function initSocleSelector() {

  const roots = document.querySelectorAll('[data-socle-selector]');
  if (!roots.length) return;

  // Survol disponible (desktop pointeur fin) → aperçu flottant transitoire.
  // Sinon (mobile / tablette tactile) → aperçu inline au clic.
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 861px)').matches;

  roots.forEach(root => {

    const swatches   = root.querySelectorAll('[data-socle-swatch]');
    const preview    = root.querySelector('[data-socle-preview]');
    const previewImg = root.querySelector('[data-socle-preview-img]');
    const form       = root.closest('[data-product-form]');
    const idInput    = form ? form.querySelector('[data-variant-id-input]') : null;

    if (!swatches.length) return;

    const showPreview = url => {
      if (!preview || !previewImg || !url) return;
      if (previewImg.getAttribute('src') !== url) previewImg.src = url;
      preview.classList.add('is-visible');
      preview.setAttribute('aria-hidden', 'false');
    };

    const hidePreview = () => {
      if (!preview) return;
      preview.classList.remove('is-visible');
      preview.setAttribute('aria-hidden', 'true');
    };

    const selectSwatch = swatch => {
      swatches.forEach(s => {
        s.classList.remove('is-active');
        s.setAttribute('aria-checked', 'false');
      });
      swatch.classList.add('is-active');
      swatch.setAttribute('aria-checked', 'true');

      // Variante réelle → champ caché lu par cart.js (panier / commande)
      const variantId = swatch.dataset.variantId;
      if (idInput && variantId) idInput.value = variantId;
    };

    swatches.forEach(swatch => {

      swatch.addEventListener('click', () => {
        selectSwatch(swatch);
        // Mobile / tablette : aperçu inline persistant sous les pastilles
        if (!canHover) showPreview(swatch.dataset.previewUrl);
      });

      // Desktop : aperçu flottant au survol / focus (transitoire)
      if (canHover) {
        swatch.addEventListener('mouseenter', () => showPreview(swatch.dataset.previewUrl));
        swatch.addEventListener('focus',      () => showPreview(swatch.dataset.previewUrl));
        swatch.addEventListener('blur',       hidePreview);
      }
    });

    if (canHover) {
      const group = root.querySelector('.product-socle__swatches');
      if (group) group.addEventListener('mouseleave', hidePreview);
    }
  });
}


function updateVariantUI(form, variant) {

  // 1. Champ caché variant_id
  const idInput = form.querySelector('[data-variant-id-input]');
  if (idInput) idInput.value = variant.id;

  // 2. Mise à jour prix
  const section      = form.closest('[data-section-type="product-gallery"]');
  const priceWrapper = section?.querySelector('[data-product-price]');
  if (priceWrapper) renderPrice(priceWrapper, variant);

  // 3. Bouton add-to-cart
  const addBtn     = form.querySelector('[data-add-to-cart]');
  const addBtnText = form.querySelector('[data-atc-text]');

  if (addBtn && addBtnText) {
    if (variant.available) {
      addBtn.disabled = false;
      addBtn.removeAttribute('aria-disabled');
      addBtn.classList.replace('btn--outline', 'btn--primary');
      addBtnText.textContent = 'Ajouter au panier';
    } else {
      addBtn.disabled = true;
      addBtn.setAttribute('aria-disabled', 'true');
      addBtnText.textContent = 'Épuisé';
    }
  }

  // 4. Synchroniser la galerie sur l'image de la variante
  if (variant.featured_image) {
    syncGalleryToVariant(variant.featured_image);
  }
}


function renderPrice(container, variant) {

  const price        = variant.price;
  const comparePrice = variant.compare_at_price;
  const onSale       = comparePrice > price;

  if (onSale) {
    container.innerHTML = `
      <div class="price-display price-display--on-sale" data-price-display>
        <span class="price-display__sale">${formatMoney(price)}</span>
        <s class="price-display__compare">${formatMoney(comparePrice)}</s>
      </div>`;
  } else {
    container.innerHTML = `
      <div class="price-display" data-price-display>
        <span class="price-display__regular">${formatMoney(price)}</span>
      </div>`;
  }
}


function syncGalleryToVariant(variantImage) {

  const wrapper = document.querySelector('[data-gallery-wrapper]');
  if (!wrapper) return;

  const thumbs = wrapper.querySelectorAll('[data-gallery-thumb]');

  const matchingThumb = Array.from(thumbs).find(thumb => {
    const url = thumb.dataset.galleryImageUrl || '';
    // Comparaison sur l'ID numérique Shopify présent dans l'URL CDN
    return url.includes(`/${variantImage.id}/`) || url.includes(`_${variantImage.id}.`);
  });

  if (matchingThumb) matchingThumb.click();
}


function formatMoney(cents) {
  if (window.EloiseTheme?.formatMoney) {
    return window.EloiseTheme.formatMoney(cents);
  }
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}
